// src/components/common/modal/ModalBase.tsx

import React, { useRef, useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  styled,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Edit as EditIcon, Close as CloseIcon } from '@mui/icons-material';
import { colors } from '../../../theme/theme';

interface ModalBaseProps {
  open: boolean;
  onClose: () => void;
  petName?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  desktopWidth?: number | string;
  mobileWidth?: number | string;
  headerIcon?: React.ReactNode;
  headerTitle?: string;
  kennelNumber?: string;
  kennelSide?: string;
  maxHeight?: number | string;
  showCloseButton?: boolean;
}

interface DragState {
  x: number;
  y: number;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  // StickyFooter (FormElements.tsx) is deliberately zIndex: 2000 so it
  // stays above scrolling page content - but that also puts it above
  // MUI's default modal zIndex (1300), so any ModalBase dialog opened on
  // a page with a visible StickyFooter would otherwise have its own
  // footer buttons overlapped/click-blocked by the page's sticky footer
  // bar. Explicitly raised above 2000 here so this applies to every
  // ModalBase-based dialog in the app, not just the one that surfaced it.
  zIndex: 2100,
  '& .MuiDialog-paper': {
    boxShadow: theme.shadows[8],
    display: 'flex',
    flexDirection: 'column',
  },
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
  },
}));

const ModalHeader = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: theme.spacing(1.5),
  borderTopLeftRadius: theme.shape.borderRadius,
  borderTopRightRadius: theme.shape.borderRadius,
  minWidth: 0,
  userSelect: 'none',
  flexShrink: 0,
}));

const HeaderIcon = styled(Box)(({ theme }) => ({
  width: '3rem',
  height: '3rem',
  opacity: 0.9,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(1),
  flexShrink: 0,
  '& svg': {
    fontSize: '2rem',
  },
}));

const ModalTitle = styled(Typography)(() => ({
  fontSize: '2rem',
  fontWeight: 600,
  color: 'inherit',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
}));

const ModalContentBox = styled(DialogContent)(({ theme }) => ({
  padding: '0 .5rem .5rem .5rem',
  overflow: 'auto',
  flex: '1 1 auto',
  minHeight: 0,
  WebkitOverflowScrolling: 'touch',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.action.hover,
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: theme.palette.action.active,
    },
  },
}));

const ModalFooterBox = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  position: 'relative',
  padding: `${theme.spacing(2)} ${theme.spacing(2)}`,
  borderTop: `3px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-24px',
    left: 0,
    right: 0,
    height: '24px',
    pointerEvents: 'none',
    background: `linear-gradient(
      to top,
      rgba(0,0,0,0.18),
      rgba(0,0,0,0.08),
      rgba(0,0,0,0)
    )`,
  },
}));

const ModalBase: React.FC<ModalBaseProps> = ({
  open,
  onClose,
  petName = '',
  children,
  footer,
  desktopWidth = '480px',
  mobileWidth = '90vw',
  headerIcon = <EditIcon />,
  headerTitle = `Edit ${petName}`,
  kennelNumber,
  kennelSide,
  maxHeight,
  showCloseButton = true,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

  const [position, setPosition] = useState<DragState>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  useEffect(() => {
    if (open) {
      setPosition({ x: 0, y: 0 });
    }
  }, [open]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isDesktop) return;
      setIsDragging(true);
      dragStart.current = {
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      };
    },
    [isDesktop, position]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;
      setPosition({
        x: e.clientX - dragStart.current.x,
        y: e.clientY - dragStart.current.y,
      });
    },
    [isDragging]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const getTitle = () => {
    if (kennelNumber) {
      const kennelPrefix = kennelSide ? `${kennelSide}` : '';
      return `${kennelPrefix}${kennelNumber} ${headerTitle}`;
    }
    return headerTitle;
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: 1,
          position: 'relative',
          overflow: showCloseButton ? 'visible' : 'hidden',
          padding: 0,
          margin: 0,
          height: 'fit-content',
          maxHeight: maxHeight || undefined,
          ...(isDesktop && {
            transform: `translate(${position.x}px, ${position.y}px)`,
            transition: isDragging ? 'none' : 'transform 0.15s ease-out',
          }),
          [theme.breakpoints.down('sm')]: {
            width: mobileWidth,
            maxWidth: mobileWidth,
          },
          [theme.breakpoints.up('sm')]: {
            width: desktopWidth,
            maxWidth: desktopWidth,
          },
        },
      }}
    >
      <ModalHeader
        onMouseDown={handleMouseDown}
        sx={
          isDesktop
            ? {
                cursor: isDragging ? 'grabbing' : 'grab',
              }
            : undefined
        }
      >
        <HeaderIcon>{headerIcon}</HeaderIcon>
        <ModalTitle>{getTitle()}</ModalTitle>
      </ModalHeader>

      {showCloseButton && (
        <IconButton
          onClick={onClose}
          onMouseDown={(e) => e.stopPropagation()}
          size="small"
          sx={{
            position: 'absolute',
            top: -4,
            right: -4,
            zIndex: 1,
            width: '2.7rem',
            height: '2.7rem',
            backgroundColor: theme.palette.primary.main,
            border: `3px solid ${theme.palette.primary.light}40`,
            boxShadow: theme.shadows[6],
            color: theme.palette.grey[0],
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
              boxShadow: colors.shadows.powerInsetBold,
              border: `3px solid ${theme.palette.primary.light}`,
            },
            '& svg': { fontSize: '2rem' },
          }}
        >
          <CloseIcon />
        </IconButton>
      )}

      <ModalContentBox sx={footer ? { paddingBottom: 0 } : undefined}>
        {children}
      </ModalContentBox>

      {footer && <ModalFooterBox>{footer}</ModalFooterBox>}
    </StyledDialog>
  );
};

export default ModalBase;
